import { css } from '@emotion/core'
import { useLazyQuery } from '@apollo/client'
import { trackEvent } from '@fs/zion-analytics'
import zionDebug from '@fs/zion-debug'
import { NoticeLoading } from '@fs/zion-icon'
import { ComboViewer, RibbonViewer, StaticImageViewer } from '@fs/zion-image-viewer'
import { useQueryParams } from '@fs/zion-router'
import { useSlsStateContext } from '@fs/zion-sls'
import { Row, useContainerWidth, useZoomPan } from '@fs/zion-ui'
import React, { useCallback, useContext, useMemo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import queries from '../../views/imageBrowse/queries'
import SharedUsabilla from '../../../shared/components/sharedUsabilla/SharedUsabilla'
import { downloadImage, getFileNames, getOptimizedImageProps } from '../../utils/downloadHelpers'
import { useErrorStatus, useGroupData } from '../../views/imageBrowse/hooks'
import { useSharedBrowserStateContext } from '../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

import useInitialMaintainZoomPan from '../../../shared/hooks/useInitialMaintainZoom'
import useViewMode from '../../views/imageBrowse/hooks/useViewMode'
import ImageGroupError from '../imageGroupError/ImageGroupError'
import Controls from './Controls'
import {
  useImageBrowserGroup,
  useImageBrowserSetup,
  useKeepGroupIdInSync,
  useSections,
  useSetFocusedIndex,
  useSetGridParamInURL,
  useUpdateDividersHoverButtonProps,
  useWatchApidsForThumbnails,
  useWatchViewModeForFocusedIndex,
  useInitializeViewModeFromURL,
} from './hooks'
import DarkModeControl from '../../../shared/components/darkModeControl/DarkModeControl'
import { DarkModeContext } from '../../../shared/providers/darkModeProvider/DarkModeProvider'

const debug = zionDebug('siv:SharedImageBrowserViewer')

const viewerContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minWidth: 0,
}

// eslint-disable-next-line no-warning-comments
// TODO: remove this when we fix JIRA FRONTIER-911
const viewerWrapperCss = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  position: relative;
`

export default function GroupImage() {
  const [t] = useTranslation()
  const { theme, useAllowDarkMode, resetDarkMode } = useContext(DarkModeContext)
  const { query } = useQueryParams({ replaceState: true })
  const [imageViewer] = useZoomPan()
  const atWidth = useContainerWidth()
  const { view = 'explore' } = query

  const groupData = useImageBrowserGroup()
  const sharedBrowserState = useSharedBrowserStateContext()
  const zionSlsState = useSlsStateContext()
  const imageApids = groupData?.imageApids
  const { thumbnails } = sharedBrowserState

  const { loading } = useGroupData()
  const { currentImageArk } = zionSlsState
  const { errorStatus } = useErrorStatus()
  const initialMaintainZoomPanProps = useInitialMaintainZoomPan()
  const [columnCount, setColumnCount] = useState(5)
  const { viewMode, setViewMode } = useViewMode()
  const [getNineFive, { data: nineFive, loading: loadNineFive }] = useLazyQuery(queries.getArtifactNineFive())

  const ribbonImages = useMemo(() => {
    return thumbnails?.map((x) => ({ alt: x.alt, id: x.id, src: x.srcSet?.[0] || '' }))
  }, [thumbnails])

  const imageLabel = (index, id) => i18n.t('GroupImage.gridItemLabel', 'Image {index} of {id}', { index, id })
  const modifiedThumbnails = useMemo(
    () => thumbnails?.map((thumb, idx) => ({ ...thumb, 'aria-label': imageLabel(idx + 1, thumb.id) })),
    [thumbnails]
  )

  useEffect(() => {
    if (thumbnails?.[sharedBrowserState.focusedIndex]) {
      const focusedImage = thumbnails?.[sharedBrowserState.focusedIndex]
      getNineFive({ variables: { imageApid: focusedImage?.id } })
    }
  }, [thumbnails, sharedBrowserState.focusedIndex, getNineFive])

  const handleDownloadEvent = useCallback(() => {
    const focusedImage = thumbnails?.[sharedBrowserState.focusedIndex]
    const { src: bestSrc } = getOptimizedImageProps(focusedImage, Infinity)
    const { downloadUrl, localFileName } = getFileNames(bestSrc)
    let finalFileName = localFileName
    if (localFileName.startsWith('image') && focusedImage?.id) {
      finalFileName = localFileName.replace('image', focusedImage.id)
    }
    const idsFromSiblings =
      groupData?.siblingIdsWithChildren?.map((sibling) => sibling.childIds).flat() || groupData?.imageApids
    const location = idsFromSiblings.indexOf(focusedImage?.id)
    if (location > -1) {
      finalFileName = loadNineFive
        ? `${groupData?.imageGroupNumber}_${(location + 1).toString().padStart(5, 0)}`
        : nineFive?.getArtifactNineFive
    }
    downloadImage(finalFileName, downloadUrl)
    trackEvent({
      link_name: 'image-download-complete',
    })
  }, [
    groupData?.imageApids,
    groupData?.imageGroupNumber,
    groupData?.siblingIdsWithChildren,
    sharedBrowserState.focusedIndex,
    thumbnails,
    loadNineFive,
    nineFive,
  ])

  /**
   * A Warning:
   * Do not be fooled by the seeming lack of arguments. The following hooks make heavy usage of context
   */
  // IMPORTANT: Call useInitializeViewModeFromURL BEFORE useImageBrowserSetup to avoid race conditions
  useInitializeViewModeFromURL()
  useImageBrowserSetup()
  useAllowDarkMode()
  useWatchViewModeForFocusedIndex({ ribbonImages })
  useWatchApidsForThumbnails()
  const updateDividersHoverButtonProps = useUpdateDividersHoverButtonProps()
  const sections = useSections()
  const setGridParamInURL = useSetGridParamInURL()
  const setFocusIndex = useSetFocusedIndex()
  const setNewGroupId = useKeepGroupIdInSync()
  const setFocusedIndex = useCallback(
    (focusedIndex) => {
      setFocusIndex(focusedIndex)
      setNewGroupId(focusedIndex)
    },
    [setFocusIndex, setNewGroupId]
  )

  const handleAdjustmentControlReset = useCallback(() => {
    resetDarkMode()
  }, [resetDarkMode])

  debug({
    currentImageArk,
    focusedIndex: sharedBrowserState.focusedIndex,
    ribbonImages,
    thumbnails,
    imageApids,
    loading,
    showImageGrid: query?.grid === 'on',
    sharedBrowserState,
  })

  // Commented out for now because of jira LCOM-1258
  // useKeyboardShortcuts({
  //   handlers: {
  //     KeyI: {
  //       action: () => infoSheetOverlay?.handleClick(),
  //       title: t('toggle-infosheet.button', 'Toggle Panel'),
  //     },
  //   },
  //   enabled: true,
  // })

  return !errorStatus ? (
    <>
      {loading ? (
        <Row alignX="center" alignY="middle">
          <NoticeLoading size="md" />
        </Row>
      ) : (
        <section
          css={viewerWrapperCss}
          data-theme={theme}
          aria-label={t('film-viewer.accessibility-heading', 'Image Viewer')}
          style={viewerContainerStyle}
        >
          <ComboViewer
            {...imageViewer}
            setFocusedIndex={setFocusedIndex}
            focusedIndex={modifiedThumbnails ? sharedBrowserState.focusedIndex : 0}
            showImageGrid={query?.grid === 'on'}
            setShowImageGrid={setGridParamInURL}
            setColumnCount={setColumnCount}
            columnCount={atWidth({ default: 3, md: columnCount })}
            images={modifiedThumbnails}
            ImageComponent={StaticImageViewer}
            adjustmentControls={<DarkModeControl />}
            onAdjustmentControlReset={handleAdjustmentControlReset}
            downloadControl
            onDownload={handleDownloadEvent}
            sections={sections}
            hoverButtonProps={updateDividersHoverButtonProps}
            controls={
              <Controls
                {...{
                  viewMode,
                  setViewMode,
                }}
              />
            }
            gridControls={
              <Controls
                {...{
                  viewMode,
                  setViewMode,
                }}
              />
            }
            numbered
            {...initialMaintainZoomPanProps}
            minZoom={0.8}
          />
          {query?.grid !== 'on' && ribbonImages.length && (
            <RibbonViewer
              focusedIndex={sharedBrowserState.focusedIndex > -1 ? sharedBrowserState.focusedIndex : 0}
              setFocusedIndex={setFocusedIndex}
              images={ribbonImages}
            />
          )}
        </section>
      )}
    </>
  ) : (
    <>
      <ImageGroupError errorState={errorStatus} view={view} />
      <SharedUsabilla />
    </>
  )
}
